import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { DashboardReportsApi } from "../DashboardReportsApi";
import { GeneratedDashboardReportResponse, SendReportRequest } from "../model/dashboard/GeneratedDashboardReportResponse";
import { ReportRequestType } from "../model/requests/ReportRequestModels";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  getRelatedCacheKeys,
  IMutationOptions,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";


export const useGetGeneratedReportData = (
  params: ReportRequestType,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [
      QueryKey.GENERATED_REPORT_DATA,
      ...params.id.map((e) => e.toString()),
      params.onlyType?.toString(),
      params.from,
      params.to,
    ],
    () => DashboardReportsApi.getGeneratedReports(params),
    null,
    options
  );

export function useDeleteReportGeneration(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse | undefined,
  unknown,
  {
    genId: number;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { genId: number }) =>
      DashboardReportsApi.deleteGeneration(data.genId).then(
        defaultResponseHandler
      ),
    onSuccess: (data) => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, (oldQueryData: any) => {
          return oldQueryData?.filter(
            (e: GeneratedDashboardReportResponse) => e.id !== data?.id
          );
        });
      });

      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
  });
}

export function useDeleteSingleReport(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse | undefined,
  unknown,
  {
    genId: number;
    reportId: string;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { genId: number; reportId: string }) =>
      DashboardReportsApi.deleteReport(data.genId, data.reportId).then(
        defaultResponseHandler
      ),
    onSuccess: (data) => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, (oldQueryData: any) => {
          return oldQueryData
            ?.map((e: GeneratedDashboardReportResponse) => {
              if (data && e.id === data.id) {
                if (data.fileIds.length > 0) {
                  return data;
                } 
                  return undefined;
                
              }
              return e;
            })
            .filter(function (item: any) {
              return typeof item !== "undefined" && item !== null;
            });
        });
      });

      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
  });
}

export function useSendReportToRecipients(
  specOptions?: IMutationOptions
): UseMutationResult<GeneratedDashboardReportResponse | undefined, unknown, SendReportRequest> {
  const options = { ...defaultMutationOptions, ...specOptions };

  return useMutation({
    mutationFn: ({ genId, reportId, recipients }: SendReportRequest) =>
      DashboardReportsApi.sendReportToRecipients(genId, reportId, recipients).then(
        defaultResponseHandler
      ),
    onSuccess: () => options.onSuccess!!(),
    onError: error => options.onFailure!!(error),
  });
}

export function useMarkForRegeneration(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse[] | undefined,
  unknown,
  {
    genId: Array<number>;
    lazy?: boolean;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { genId: Array<number>; lazy?: boolean }) =>
      DashboardReportsApi.markForRegeneration({
        genId: [...data.genId],
        lazyRegen: data.lazy || false,
      }).then(defaultResponseHandler),

    onSuccess: (data) => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        const updatedData = data
          ? new Map(
              data.map((item: GeneratedDashboardReportResponse) => [
                item.id,
                item,
              ])
            )
          : null;

        queryClient.setQueryData(
          key,
          (oldQueryData: GeneratedDashboardReportResponse[] | undefined) => {
            if (!oldQueryData) {
              return [];
            }

            return oldQueryData.map(
              (item: GeneratedDashboardReportResponse) => {
                const updatedItem = updatedData?.get(item.id);
                return updatedItem || item;
              }
            );
          }
        );
      });

      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
  });
}

export function useMarkForRegenerationRange(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse[] | undefined,
  unknown,
  {
    dashboardId: number;
    from: string;
    to: string;
    fillMissingSlots?: boolean;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      dashboardId: number;
      from: string;
      to: string;
      fillMissingSlots?: boolean;
    }) =>
      DashboardReportsApi.markForRegenerationRange({ ...data }).then(
        defaultResponseHandler
      ),

    onSuccess: () => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        queryClient.refetchQueries({ queryKey: key });
      });

      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
  });
}

export function useGenerateReport(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse | undefined,
  unknown,
  {
    dashboardId: number;
    referenceTime: string;
    generateNearest?: boolean;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      dashboardId: number;
      referenceTime: string;
      generateNearest?: boolean;
    }) =>
      DashboardReportsApi.generateReport({
        dashboardId: data.dashboardId,
        referenceTime: data.referenceTime,
        generateNearest: data.generateNearest,
      }).then(defaultResponseHandler),

    onSuccess: (data) => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, (oldQueryData: any) => {
          if (!data && (!oldQueryData || oldQueryData.length === 0)) {
            return [];
          }

          if (!data && oldQueryData) {
            return oldQueryData;
          }

          if (data && (!oldQueryData || oldQueryData.length === 0)) {
            return [data];
          }

          let i = oldQueryData.length - 1;

          while (
            i >= 0 &&
            (new Date(data.referenceTime) >
              new Date(oldQueryData[i].referenceTime) ||
              (new Date(data.referenceTime).getTime() ===
                new Date(oldQueryData[i].referenceTime).getTime() &&
                new Date(data.generationTimeStart) >
                  new Date(oldQueryData[i].generationTimeStart)))
          ) {
            i--;
          }

          oldQueryData.splice(i + 1, 0, data);

          return oldQueryData;
        });
      });
      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
  });
}

export function useDeleteReportRange(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse[] | undefined,
  unknown,
  {
    dashboardId: number;
    from: string;
    to: string;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { dashboardId: number; from: string; to: string }) =>
      DashboardReportsApi.deleteReportsRange(data.dashboardId, {
        from: data.from,
        to: data.to,
      }).then(defaultResponseHandler),
    onSuccess: (data) => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, (oldQueryData: any) => {
          return oldQueryData
            ?.map((e: GeneratedDashboardReportResponse) => {
              if (data && data.some((ob) => ob.id === e.id)) {
                return undefined;
              }
              return e;
            })
            .filter(function (item: any) {
              return typeof item !== "undefined" && item !== null;
            });
        });
      });

      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
  });
}

export function useCleanupReportHistoryRange(
  specOptions?: IMutationOptions
): UseMutationResult<
  GeneratedDashboardReportResponse[] | undefined,
  unknown,
  {
    dashboardId: number;
    params: { from: string; to: string };
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      dashboardId: number;
      params: { from: string; to: string };
    }) =>
      DashboardReportsApi.cleanupReportHistoryRange(
        data.dashboardId,
        data.params
      ).then(defaultResponseHandler),
    onSuccess: (data) => {
      const queryKeys = getRelatedCacheKeys(
        queryClient,
        QueryKey.GENERATED_REPORT_DATA
      );
      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, (oldQueryData: any) => {
          return oldQueryData.map((obj: GeneratedDashboardReportResponse) => {
            return data?.find((ob) => ob.id === obj.id) || obj;
          });
        });
      });

      options.onSuccess!!();
    },
    onError: (error) => {
      options.onFailure!!(error);
    },
    onMutate: (variables) => {
      options.onMutate!!(variables);
    },
  });
}


